import { ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Grid, Link, Typography } from '@mui/material'

import FullScreenModal from '../../components/full-screen-modal'
import useIsMobile from '../../utils/hooks/useIsMobile'

import classes from './index.styles'

const TroubleshootingModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const isMobile = useIsMobile()

  return (
    <FullScreenModal open={isOpen} onClose={onClose} title="Troubleshoot" showCloseIcon={true} confirmLabel="OK" onConfirm={onClose}>
      <Grid container={true} sx={classes.troubleshootingMainContainer}>
        <Grid container={true} sx={classes.troubleshootingSection}>
          <Grid>
            <Typography variant="subtitle1">Fixing Connection Issues</Typography>

            <Accordion sx={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" sx={classes.sectionTitle}>
                  Restart the device
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={classes.accordionDetails}>
                <Typography variant="body2">Remove the battery, wait at least 10 seconds, then reinsert it to force a connection attempt.</Typography>

                <Typography component="ul" variant="body2" color="text.secondary" sx={classes.listContainer}>
                  <li>Slow Blinking Red: The device failed to connect to the network.</li>
                  <li>Fast Blinking Red: The device has a weak cellular signal.</li>
                  <li>Success: LED blinks green briefly, then turns off, indicating a successful connection.</li>
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <b>Note:</b> If you don’t see any lights, there may be an issue with the battery. Replace with a fresh CR123a battery.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" sx={classes.sectionTitle}>
                  Optimize Placement
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={classes.accordionDetails}>
                <Typography variant="body2" color="text.secondary" sx={classes.descriptionText}>
                  If your kit includes a leak probe, mount the sensor on a wall (or another elevated surface), then place the attached probe on the floor to
                  detect water.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={classes.descriptionText}>
                  Alternatively, place the device in an open area, away from thick walls, metal objects, and household appliances that may interfere with signal
                  strength.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={classes.descriptionText}>
                  <b>Note:</b> If issues persist, you may consider moving the device to a new location with adequate cellular coverage. To check network
                  availability, force connection attempt by removing and reinserting the battery.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid>
            <Typography variant="subtitle1">Battery Life & Low Signal</Typography>
            <Typography variant="body2" color="text.secondary" sx={classes.noteText}>
              Your sensor is functional, however poor signal may lead to faster battery drain.
            </Typography>

            <Accordion sx={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" sx={classes.sectionTitle}>
                  To maximize battery life:
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={classes.accordionDetails}>
                <Typography component="ul" variant="body2" color="text.secondary">
                  <li>Avoid placing it in areas with known connectivity issues.</li>
                  <li>If battery depletes unusually fast, replace it with a fresh CR123a battery and you may consider optimizing device placement.</li>
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid sx={classes.helpContainer(isMobile)}>
            <Typography variant="subtitle2">Need more help?</Typography>

            <Typography variant="body2" color="text.secondary" sx={classes.noteText}>
              Contact Sensor Support (844) 468-1866
              <br />
              Hours: Monday – Friday, 8 am – 8 pm EST
              <br />
              Email: <Link href="mailto:sensorsupport@hsb.com">sensorsupport@hsb.com</Link>
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </FullScreenModal>
  )
}

export default TroubleshootingModal
